
import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Camera, Edit, Send, Loader2 } from "lucide-react";
import { ShareDialog } from "./ShareDialog";
import { ShareLinkData } from "../../pt/utils/interfaces";

interface ClientAvatarProps {
  name: string;
  clientID: string;
  onStartScan: () => void;
  onSendScanLink: () => Promise<string | undefined>;
  shared?: ShareLinkData;
  onShare: (enabled:boolean) => Promise<ShareLinkData>;
  age?: number;
  weight?: string;
  weightUnit?: string;
  imageUrl?: string;
  lastScan?: string;
  onEdit?: () => void;
}

export const ClientAvatar = ({ 
  name,
  clientID,
  onStartScan,
  onSendScanLink,
  shared,
  onShare,
  age, 
  weight, 
  weightUnit = "kg", 
  imageUrl, 
  lastScan, 
  onEdit 
}: ClientAvatarProps) => {
    const weightValue = weight ? parseFloat(weight) : 0;
    const [isSending, setIsSending] = useState(false);
  return (
    <Card className="p-6 backdrop-blur-lg bg-card/80 border border-gray-200/20 animate-fade-in">
         <div className="absolute top-4 right-4 z-10">
        <ShareDialog onShare= {onShare} shared={shared} clientName={name} clientID={clientID}  />
      </div>
      <div className="flex flex-col items-center space-y-4">
        {imageUrl ? (
          <div className="relative w-32 h-32 rounded-full overflow-hidden">
            <img
              src={imageUrl}
              alt={name}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center">
            <Camera className="w-8 h-8 text-gray-400" />
          </div>
        )}
        <div className="text-center">
          <h2 className="text-xl font-semibold">{name}</h2>
          <div className="space-y-1 mt-2">
            {age && (
              <p className="text-sm text-gray-600">Age: {age} years</p>
            )}
            {weightValue>0  && (
              <p className="text-sm text-gray-600">Weight: {weight} {weightUnit}</p>
            )}
            {lastScan && (
              <p className="text-sm text-gray-500">Last scan: {lastScan}</p>
            )}
          </div>
        </div>
        <div className="flex space-x-2 w-full">
          {onEdit && (
            <Button variant="outline" size="sm" onClick={onEdit} className="flex-1">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          <Button size="sm" className="flex-1" onClick={() => onStartScan()}>
            <Camera className="w-4 h-4 mr-2" />
            Start Scan
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            disabled={isSending}
            onClick={async () => {
              setIsSending(true);
              try {
                const scanUrl = await onSendScanLink();
                if (!scanUrl) return;
                if (navigator.share) {
                  await navigator.share({
                    title: `Scan for ${name}`,
                    url: scanUrl,
                  });
                } else {
                  await navigator.clipboard.writeText(scanUrl);
                  alert('Scan link copied to clipboard');
                }
              } catch (e: any) {
                if (e.name !== 'AbortError') console.error('Share failed:', e);
              } finally {
                setIsSending(false);
              }
            }}
          >
            {isSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Scan
          </Button>
        </div>
      </div>
    </Card>
  );
};
